import { useState, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Star,
  ExternalLink,
  Copy,
  FileSpreadsheet,
  Loader2,
  Search as SearchIcon,
  Mail,
  MailSearch,
  UserRound,
  UserSearch,
  Gauge,
  MessageSquareQuote,
  FileText,
  Grid3x3,
  MailQuestion,
  HeartHandshake,
  Table as TableIcon,
  Map as MapIcon,
  MessageSquareText,
  History,
  X,
  Eye,
  Send,
} from "lucide-react";
import { reportOpenStatus } from "@/lib/reportViews";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { LEAD_STATUSES, STATUS_MAP } from "@/constants/statuses";
import { MapView } from "@/components/MapView";
import { MessageDialog } from "@/components/MessageDialog";
import { AuditDetailDialog } from "@/components/AuditDetailDialog";
import { BulkOutreachDialog } from "@/components/BulkOutreachDialog";

const WebsiteStatus = ({ b }) => {
  if (b.has_website) {
    return (
      <div className="flex flex-col gap-1">
        <Badge className="w-fit bg-[hsl(var(--has-bg))] text-[hsl(var(--has-fg))] border border-[hsl(var(--has-border))] hover:bg-[hsl(var(--has-bg))]">
          Var
        </Badge>
        <a
          href={b.website}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-primary hover:underline truncate max-w-[150px] inline-flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3 shrink-0" />
          {b.website.replace(/^https?:\/\//, "")}
        </a>
      </div>
    );
  }
  return (
    <Badge
      data-testid={`website-status-${b.place_id}`}
      className="w-fit bg-[hsl(var(--lead-bg))] text-[hsl(var(--lead-fg))] border border-[hsl(var(--lead-border))] hover:bg-[hsl(var(--lead-bg))] font-semibold"
    >
      YOK · LEAD
    </Badge>
  );
};

// Low score = weak digital presence = the best prospect. Colours follow that
// reading: red is an opportunity, not a warning.
const scoreTone = (s) =>
  s <= 25 ? { bg: "hsl(0,72%,96%)", fg: "hsl(0,72%,38%)", bar: "hsl(0,72%,50%)" }
  : s <= 50 ? { bg: "hsl(28,85%,96%)", fg: "hsl(28,85%,34%)", bar: "hsl(28,85%,52%)" }
  : s <= 75 ? { bg: "hsl(45,90%,96%)", fg: "hsl(38,80%,32%)", bar: "hsl(45,90%,48%)" }
  : { bg: "hsl(142,50%,96%)", fg: "hsl(142,60%,28%)", bar: "hsl(142,55%,42%)" };

// "Did they even open what I sent them?" — the owner could not answer this
// before (2026-09-28 request). No badge at all before a report link exists;
// a quiet "görülmedi" once it does but nobody real has opened it yet, so a
// silent prospect is visually distinct from one who was never sent anything.
const ViewStatus = ({ b }) => {
  if (!b.report_token) return null;
  const status = reportOpenStatus(b);
  if (!status.opened) {
    return (
      <span className="text-[10px] text-muted-foreground" data-testid={`view-status-${b.place_id}`}>
        görülmedi
      </span>
    );
  }
  const last = new Date(status.lastAt);
  const dateLabel = Number.isNaN(last.getTime()) ? "" : last.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--has-bg))] px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--has-fg))]"
      title={`${status.count}× görüntülendi, son: ${Number.isNaN(last.getTime()) ? status.lastAt : last.toLocaleString("tr-TR")}`}
      data-testid={`view-status-${b.place_id}`}
    >
      <Eye className="h-3 w-3" /> {dateLabel}
    </span>
  );
};

const ScoreCell = ({ b, onOpenDetail }) => {
  if (b.digital_score == null) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  const t = scoreTone(b.digital_score);
  const critical = (b.audit_findings || []).filter(
    (f) => f.severity === "critical"
  ).length;
  return (
    <button
      onClick={() => onOpenDetail(b)}
      className="w-full text-left group"
      title="Bulguları gör"
      data-testid={`score-cell-${b.place_id}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex h-7 min-w-[2.25rem] items-center justify-center rounded-md px-1.5 text-sm font-bold tabular-nums"
          style={{ background: t.bg, color: t.fg }}
        >
          {b.digital_score}
        </span>
        {critical > 0 && (
          <span className="text-[10px] font-medium text-destructive">
            {critical} kritik
          </span>
        )}
        {b.deep_report_request && (
          <span
            className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
            title="Detaylı analiz talep etti"
          >
            <MailQuestion className="h-3 w-3" /> talep
          </span>
        )}
        {b.support_request && (
          <span
            className="inline-flex items-center gap-0.5 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground"
            title="Ek hizmet talebi var — Destek sekmesinden"
          >
            <HeartHandshake className="h-3 w-3" /> destek
          </span>
        )}
      </div>
      <div className="mt-1 h-1 w-full max-w-[90px] overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${b.digital_score}%`, background: t.bar }}
        />
      </div>
    </button>
  );
};

const StatusSelect = ({ b, onStatusChange }) => {
  const st = STATUS_MAP[b.status || "new"];
  return (
    <Select value={b.status || "new"} onValueChange={(v) => onStatusChange(b.place_id, v)}>
      <SelectTrigger
        data-testid={`status-select-${b.place_id}`}
        className="h-8 w-[140px] border-0 font-medium"
        style={{ background: st.bg, color: st.fg }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEAD_STATUSES.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const ResultsTable = ({
  results,
  onlyLeads,
  setOnlyLeads,
  onExport,
  exporting,
  onFindEmails,
  findingEmails,
  onFindOwners,
  findingOwners,
  onAudit,
  auditing,
  onReviewAnalysis,
  analysingReviews,
  benchmark,
  onStatusChange,
  onConsentChange,
  onCreateReport,
  onCreateGridRank,
  onUpdateManualGbpReview,
  onAnalyzeGbpText,
  onSuggestPhotoCategories,
  onGatherWideReport,
  onGenerateWideReport,
  onGatherContentOpportunities,
  onGenerateContentRecommendations,
  onUpdateWideReportNarrative,
  onApproveWideReport,
  onCompleteReport,
  onBulkStatus,
  onPrepareOutreach,
  onSendOutreach,
}) => {
  const [term, setTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("table");
  const [newOnly, setNewOnly] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("called");
  const [applying, setApplying] = useState(false);
  // Store just the id and re-derive from `results` on every render, so the
  // open dialog always reflects live updates (consent changes, a report
  // token created just now, a fresh audit score) instead of a snapshot
  // frozen at the moment the row's button was clicked.
  const [msgPlaceId, setMsgPlaceId] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [auditPlaceId, setAuditPlaceId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const msgBusiness = useMemo(
    () => (results || []).find((r) => r.place_id === msgPlaceId) || null,
    [results, msgPlaceId]
  );
  const auditBusiness = useMemo(
    () => (results || []).find((r) => r.place_id === auditPlaceId) || null,
    [results, auditPlaceId]
  );
  const [bulkOutreachOpen, setBulkOutreachOpen] = useState(false);
  const [sortByScore, setSortByScore] = useState(false);
  const [creatingReport, setCreatingReport] = useState(() => new Set());
  const [creatingGridRank, setCreatingGridRank] = useState(() => new Set());

  const emailsFound = useMemo(() => (results || []).filter((r) => r.email).length, [results]);
  const ownersFound = useMemo(() => (results || []).filter((r) => r.owner).length, [results]);

  const filtered = useMemo(() => {
    let rows = results || [];
    if (onlyLeads) rows = rows.filter((r) => !r.has_website);
    if (newOnly) rows = rows.filter((r) => !r.seen_before);
    if (statusFilter !== "all") rows = rows.filter((r) => (r.status || "new") === statusFilter);
    const t = term.trim().toLowerCase();
    if (t) {
      rows = rows.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(t) ||
          (r.category || "").toLowerCase().includes(t) ||
          (r.address || "").toLowerCase().includes(t) ||
          (r.email || "").toLowerCase().includes(t) ||
          (r.owner || "").toLowerCase().includes(t)
      );
    }
    if (sortByScore) {
      // Weakest digital presence first — those are the best prospects.
      rows = [...rows].sort(
        (a, b) => (a.digital_score ?? 999) - (b.digital_score ?? 999)
      );
    }
    return rows;
  }, [results, onlyLeads, newOnly, statusFilter, term, sortByScore]);

  const auditedCount = useMemo(
    () => (results || []).filter((r) => r.digital_score != null).length,
    [results]
  );

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success(`${label} kopyalandı`, { description: text });
  };

  const toggleRow = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.place_id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((r) => next.delete(r.place_id));
      } else {
        filtered.forEach((r) => next.add(r.place_id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const applyBulk = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setApplying(true);
    try {
      await onBulkStatus(ids, bulkStatus);
      const label = STATUS_MAP[bulkStatus].label;
      toast.success(`${ids.length} firma "${label}" olarak işaretlendi`);
      clearSelection();
    } catch (e) {
      toast.error("Toplu güncelleme başarısız");
    } finally {
      setApplying(false);
    }
  };

  const openMessage = (b) => {
    setMsgPlaceId(b.place_id);
    setMsgOpen(true);
  };

  const openAudit = (b) => {
    setAuditPlaceId(b.place_id);
    setAuditOpen(true);
  };

  const handleCreateReport = async (b) => {
    setCreatingReport((prev) => new Set(prev).add(b.place_id));
    try {
      const { url } = await onCreateReport(b);
      let copied = true;
      try {
        await navigator.clipboard?.writeText(url);
      } catch (e) {
        copied = false; // report link was still created — clipboard is a separate concern
      }
      toast.success(copied ? "Rapor linki kopyalandı" : "Rapor oluşturuldu", {
        description: url,
        action: { label: "Aç", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("Rapor oluşturulamadı", {
        description: "Önce 'Dijital Tarama' çalıştırılmış olmalı.",
      });
    } finally {
      setCreatingReport((prev) => {
        const next = new Set(prev);
        next.delete(b.place_id);
        return next;
      });
    }
  };

  const handleCreateGridRank = async (b) => {
    setCreatingGridRank((prev) => new Set(prev).add(b.place_id));
    try {
      await onCreateGridRank(b);
      toast.success("Isı haritası oluşturuldu", {
        description: "Rapor sayfasında 4×4 harita görünecek.",
      });
    } catch (e) {
      toast.error("Isı haritası oluşturulamadı", {
        description: e?.response?.data?.detail || "DataForSEO isteği başarısız oldu.",
      });
    } finally {
      setCreatingGridRank((prev) => {
        const next = new Set(prev);
        next.delete(b.place_id);
        return next;
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Toolbar row 1: view toggle + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border bg-card p-0.5">
          <button
            data-testid="view-table-btn"
            onClick={() => setView("table")}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
              view === "table" ? "bg-secondary font-medium" : "text-muted-foreground"
            }`}
          >
            <TableIcon className="h-4 w-4" /> Tablo
          </button>
          <button
            data-testid="view-map-btn"
            onClick={() => setView("map")}
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
              view === "map" ? "bg-secondary font-medium" : "text-muted-foreground"
            }`}
          >
            <MapIcon className="h-4 w-4" /> Harita
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Switch id="only-leads" checked={onlyLeads} onCheckedChange={setOnlyLeads} data-testid={TESTIDS.onlyLeadsSwitch} />
          <Label htmlFor="only-leads" className="text-sm cursor-pointer">Sadece Lead&apos;ler</Label>
        </div>

        <div className="flex items-center gap-2">
          <Switch id="new-only" checked={newOnly} onCheckedChange={setNewOnly} data-testid="new-only-switch" />
          <Label htmlFor="new-only" className="text-sm cursor-pointer">Yeni firmalar</Label>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm durumlar</SelectItem>
            {LEAD_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative w-full sm:w-52">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid={TESTIDS.tableSearchInput}
            className="pl-8 h-9"
            placeholder="Tabloda ara…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Toolbar row 2: the audit is the primary action — it drives everything else */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-accent/50 px-3 py-2.5">
        <Button
          size="sm"
          onClick={onAudit}
          disabled={auditing || !results?.length}
          data-testid="audit-button"
        >
          {auditing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />}
          Dijital Tarama
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onReviewAnalysis(Array.from(selected))}
          disabled={analysingReviews || selected.size === 0}
          title={selected.size === 0 ? "Önce satır seçin (her firma bir API çağrısı)" : ""}
          data-testid="review-analysis-button"
        >
          {analysingReviews ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquareQuote className="h-4 w-4" />}
          Yorum Analizi{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
        {auditedCount > 0 && (
          <>
            <div className="flex items-center gap-2">
              <Switch
                id="sort-score"
                checked={sortByScore}
                onCheckedChange={setSortByScore}
                data-testid="sort-score-switch"
              />
              <Label htmlFor="sort-score" className="cursor-pointer text-sm">
                En zayıflar önce
              </Label>
            </div>
            <span className="text-xs text-muted-foreground">
              {auditedCount} firma tarandı
              {benchmark?.score_median != null && ` · kohort medyanı ${benchmark.score_median}/100`}
            </span>
          </>
        )}
      </div>

      {/* Toolbar row 3: enrichment + export */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onFindEmails}
            disabled={findingEmails || !results?.some((r) => r.has_website)}
            data-testid="find-emails-button"
          >
            {findingEmails ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailSearch className="h-4 w-4" />}
            E-postaları Bul
          </Button>
          {emailsFound > 0 && (
            <span className="text-xs text-muted-foreground">{emailsFound} e-posta bulundu</span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onFindOwners}
            disabled={findingOwners || !results?.some((r) => r.has_website)}
            data-testid="find-owners-button"
          >
            {findingOwners ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserSearch className="h-4 w-4" />}
            Sahiplerini Bul
          </Button>
          {ownersFound > 0 && (
            <span className="text-xs text-muted-foreground">{ownersFound} sahip bulundu</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => onExport("all")} disabled={exporting || !results?.length} data-testid={TESTIDS.exportAll}>
            {exporting === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel (Tümü)
          </Button>
          <Button size="sm" onClick={() => onExport("no_website")} disabled={exporting || !results?.some((r) => !r.has_website)} data-testid={TESTIDS.exportLeads}>
            {exporting === "no_website" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel (Sadece Lead)
          </Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && view === "table" && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-accent px-3 py-2.5"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium">{selected.size} firma seçili</span>
          <span className="text-sm text-muted-foreground">→ durumu değiştir:</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="bulk-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEAD_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={applyBulk} disabled={applying} data-testid="bulk-apply-button">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Uygula
          </Button>
          {onPrepareOutreach && onSendOutreach && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkOutreachOpen(true)}
              data-testid="bulk-outreach-button"
            >
              <Send className="h-4 w-4" /> Toplu E-posta Gönder
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-button">
            <X className="h-4 w-4" /> Seçimi Temizle
          </Button>
        </div>
      )}

      {view === "map" ? (
        <MapView results={filtered} />
      ) : (
        <>
          <div className="rounded-xl border bg-card shadow-[var(--shadow-card)] overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid={TESTIDS.resultsTable} className="min-w-[1590px]">
                <TableHeader>
                  <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                    <TableHead className="w-[44px]">
                      <Checkbox
                        checked={allFilteredSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Tümünü seç"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="w-[260px]">İşletme</TableHead>
                    <TableHead className="w-[120px]">Dijital Puan</TableHead>
                    <TableHead className="w-[100px]">Puan</TableHead>
                    <TableHead className="w-[140px]">Telefon</TableHead>
                    <TableHead className="w-[170px]">E-posta</TableHead>
                    <TableHead className="w-[160px]">Web Sitesi</TableHead>
                    <TableHead className="w-[150px]">Lead Durumu</TableHead>
                    <TableHead className="w-[90px]">Rapor</TableHead>
                    <TableHead className="w-[190px] text-center">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((b) => {
                    const isLead = !b.has_website;
                    const isSel = selected.has(b.place_id);
                    return (
                      <TableRow
                        key={b.place_id}
                        data-testid={`results-row-${b.place_id}`}
                        className={`transition-colors hover:bg-[hsl(var(--table-row-hover))] ${
                          isSel ? "bg-accent" : isLead ? "bg-[hsl(var(--lead-bg)/0.35)] border-l-[3px] border-l-[hsl(var(--lead-border))]" : ""
                        }`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSel}
                            onCheckedChange={() => toggleRow(b.place_id)}
                            aria-label="Seç"
                            data-testid={`row-checkbox-${b.place_id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <span className="leading-snug">{b.name}</span>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-normal text-muted-foreground">
                            {b.category && <span className="truncate">{b.category}</span>}
                            {b.owner && (
                              <button
                                onClick={() => copy(b.owner, "İsim")}
                                className="inline-flex items-center gap-1 hover:text-primary"
                                title="Kopyala"
                              >
                                <UserRound className="h-3 w-3 shrink-0" />
                                <span className="truncate">{b.owner}</span>
                              </button>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {b.seen_before && (
                              <span
                                data-testid={`seen-badge-${b.place_id}`}
                                className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-secondary"
                              >
                                <History className="h-3 w-3" /> Daha önce eklendi
                              </span>
                            )}
                            {b.business_status && b.business_status !== "OPERATIONAL" && (
                              <span className="text-[10px] text-destructive">Kapalı / Geçici</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <ScoreCell b={b} onOpenDetail={openAudit} />
                        </TableCell>
                        <TableCell>
                          {b.rating != null ? (
                            <span className="inline-flex items-center gap-1 tabular-nums text-sm">
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              {b.rating}
                              <span className="text-muted-foreground">({b.review_count})</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {b.phone ? (
                            <button onClick={() => copy(b.phone, "Telefon")} className="group inline-flex items-center gap-1.5 text-sm hover:text-primary" title="Kopyala">
                              <span className="font-mono text-xs">{b.phone}</span>
                              <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {b.email ? (
                            <button onClick={() => copy(b.email, "E-posta")} className="group inline-flex items-center gap-1.5 text-xs hover:text-primary max-w-[160px]" title="Kopyala">
                              <Mail className="h-3.5 w-3.5 shrink-0 text-primary" />
                              <span className="truncate">{b.email}</span>
                            </button>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell><WebsiteStatus b={b} /></TableCell>
                        <TableCell><StatusSelect b={b} onStatusChange={onStatusChange} /></TableCell>
                        <TableCell><ViewStatus b={b} /></TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary"
                              onClick={() => handleCreateReport(b)}
                              disabled={creatingReport.has(b.place_id) || b.digital_score == null}
                              title={b.digital_score == null ? "Önce Dijital Tarama çalıştırın" : "Rapor linki oluştur"}
                              data-testid={`report-btn-${b.place_id}`}
                            >
                              {creatingReport.has(b.place_id) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary"
                              onClick={() => handleCreateGridRank(b)}
                              disabled={creatingGridRank.has(b.place_id) || !b.report_token}
                              title={!b.report_token ? "Önce rapor oluşturun" : "Isı Haritası Oluştur (~$0.03, 16 nokta)"}
                              data-testid={`grid-rank-btn-${b.place_id}`}
                            >
                              {creatingGridRank.has(b.place_id) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Grid3x3 className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary"
                              onClick={() => openMessage(b)}
                              title="Mesaj taslağı"
                              data-testid={`message-btn-${b.place_id}`}
                            >
                              <MessageSquareText className="h-4 w-4" />
                            </Button>
                            {b.google_maps_url && (
                              <a
                                href={b.google_maps_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`maps-link-${b.place_id}`}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary text-primary"
                                aria-label="Google Maps'te aç"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {filtered.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">Bu filtreye uygun sonuç yok.</div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {filtered.length} işletme gösteriliyor{onlyLeads ? " (sadece lead)" : ""}{newOnly ? " · sadece yeni" : ""}.
          </p>
        </>
      )}

      <MessageDialog
        business={msgBusiness}
        benchmark={benchmark}
        open={msgOpen}
        onOpenChange={setMsgOpen}
        onEnsureReport={onCreateReport}
        onCompleteReport={onCompleteReport}
        onConsentChange={onConsentChange}
      />
      <AuditDetailDialog
        business={auditBusiness}
        benchmark={benchmark}
        open={auditOpen}
        onOpenChange={setAuditOpen}
        onUpdateManualGbpReview={onUpdateManualGbpReview}
        onAnalyzeGbpText={onAnalyzeGbpText}
        onSuggestPhotoCategories={onSuggestPhotoCategories}
        onGatherWideReport={onGatherWideReport}
        onGenerateWideReport={onGenerateWideReport}
        onGatherContentOpportunities={onGatherContentOpportunities}
        onGenerateContentRecommendations={onGenerateContentRecommendations}
        onUpdateWideReportNarrative={onUpdateWideReportNarrative}
        onApproveWideReport={onApproveWideReport}
      />
      {onPrepareOutreach && onSendOutreach && (
        <BulkOutreachDialog
          open={bulkOutreachOpen}
          onOpenChange={setBulkOutreachOpen}
          businesses={(results || []).filter((r) => selected.has(r.place_id))}
          onPrepare={onPrepareOutreach}
          onSend={onSendOutreach}
        />
      )}
    </div>
  );
};
